import { SEVERITIES_FILTER_OPTIONS } from "@/const/options.constants"
import { STRICT_EQUALITY_SYMBOL } from "@/organisms/SmartSearch/const/symbol.constants"

import type { TFunction } from "@/hooks/useTranslation"
import type { OptionFilter } from "@/organisms/SmartSearch/types/options.types"

export const InventoryFilters = {
  application: (t: TFunction) =>
    ({
      key: "applicationName",
      label: t("filters.application"),
      type: "input",
      helper: [
        {
          label: t("inventory.generalFilters.multipleValues", {
            label: "application"
          }),
          description: t("inventory.generalFilters.multipleSubstring", {
            name: "applications"
          })
        }
      ]
    }) as OptionFilter,
  environment: (t: TFunction, options?: { label: string; value: string }[]) =>
    ({
      key: "environment",
      label: t("filters.environment"),
      type: "multiple",
      options: options || []
    }) as OptionFilter,
  namespace: (t: TFunction) =>
    ({
      key: "namespace",
      label: t("filters.namespace"),
      type: "input",
      helper: [
        {
          label: t("inventory.generalFilters.multipleValues", {
            label: "namespace"
          }),
          description: t("inventory.generalFilters.multipleSubstring", {
            name: "namespaces"
          })
        }
      ]
    }) as OptionFilter,
  repositoryName: (
    t: TFunction,
    { strictEquality }: { strictEquality?: boolean } = {}
  ) =>
    ({
      key: strictEquality ? "repositoryNameStrict" : "repositoryName",
      label: strictEquality
        ? `${STRICT_EQUALITY_SYMBOL}${t("filters.codeRepository")}`
        : t("filters.codeRepository"),
      type: "input",
      linkedKey: "repositoryName",
      strictEquality: !!strictEquality,
      helper: [
        {
          label: `${strictEquality ? STRICT_EQUALITY_SYMBOL : ""}${t(
            "inventory.generalFilters.multipleValues",
            { label: "code-repository" }
          )}`,
          description: strictEquality
            ? t("filters.helpers.strictEquality")
            : t("inventory.generalFilters.multipleSubstring", {
                name: "code repositories"
              })
        }
      ]
    }) as OptionFilter,
  imageName: (t: TFunction) =>
    ({
      key: "imageName",
      label: t("filters.image"),
      type: "input",
      helper: [
        {
          label: t("filters.helpers.multipleValues", {
            label: t("filters.image")
          }),
          description: t("inventory.generalFilters.multipleSubstring", {
            name: "images"
          })
        }
      ]
    }) as OptionFilter,
  packageManagers: (t: TFunction) =>
    ({
      key: "packageManagers",
      label: t("filters.packageManager"),
      type: "multiple",
      helper: [
        {
          label: t("filters.helpers.multipleValues", {
            label: t("filters.packageManager")
          }),
          description: t("inventory.generalFilters.multipleSubstring", {
            name: "package managers"
          })
        }
      ],
      options: [
        {
          label: t("packageManagers.PACKAGE_MANAGER_UNSPECIFIED"),
          value: "unspecified"
        },
        t("packageManagers.PACKAGE_MANAGER_PIP"),
        t("packageManagers.PACKAGE_MANAGER_POETRY"),
        t("packageManagers.PACKAGE_MANAGER_GO_MODULES"),
        t("packageManagers.PACKAGE_MANAGER_RUBYGEMS"),
        t("packageManagers.PACKAGE_MANAGER_MAVEN"),
        t("packageManagers.PACKAGE_MANAGER_COMPOSER"),
        t("packageManagers.PACKAGE_MANAGER_YARN"),
        t("packageManagers.PACKAGE_MANAGER_PNPM"),
        t("packageManagers.PACKAGE_MANAGER_GRADLE"),
        t("packageManagers.PACKAGE_MANAGER_NPM"),
        t("packageManagers.PACKAGE_MANAGER_SBT"),
        t("packageManagers.PACKAGE_MANAGER_NUGET"),
        t("packageManagers.PACKAGE_MANAGER_UV"),
        t("packageManagers.PACKAGE_MANAGER_UPM"),
        t("packageManagers.PACKAGE_MANAGER_BUN"),
        t("packageManagers.PACKAGE_MANAGER_OHPM")
      ].map((value) => {
        if (typeof value === "string") {
          return { label: value, value }
        }

        return value
      })
    }) as OptionFilter
}

export const getSeverityFilter = () => {
  return {
    key: "severity",
    label: "severity",
    type: "multiple",
    options: [...SEVERITIES_FILTER_OPTIONS]
  } as OptionFilter
}

export const getRuntimeFilter = () => {
  return {
    key: "runtime",
    order: 0,
    label: "runtime",
    type: "single",
    options: [
      { label: "Yes", value: "YES", disabled: false },
      { label: "No", value: "NO", disabled: true }
    ]
  } as OptionFilter
}
